import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from './store/auth'
import { firstStop } from './lib/firstStop'
import { useBuddies } from './store/buddies'
import { useGifts } from './store/gifts'
import { useChurchCheckIns } from './store/churchCheckIns'
import { useChurchReplies } from './store/churchReplies'
import { useReminders } from './store/reminders'
import { useSeason } from './store/season'
import { useCatalog } from './store/catalog'
import { useJuiceSync } from './juice/useJuice'
import { inAppPath, initNative, refreshPushToken } from './lib/native'

import Landing from './pages/Landing'
import Onboarding from './features/onboarding/Onboarding'
import AuthScreen from './features/auth/AuthScreen'
import HomeScreen from './features/home/HomeScreen'
import QuizScreen from './features/daily/QuizScreen'
import ResultScreen from './features/daily/ResultScreen'
import { BattleResume } from './features/arena/BattleResume'
import { ChurchResume } from './features/church/ChurchResume'
import { setPendingChurchAdmin } from './features/church/pending'
import { isSupabaseConfigured } from './lib/config'
import { StudyDropToast } from './features/study/StudyDropToast'
import { WaystationToast } from './features/season/WaystationToast'
import { SkinUnlockToast } from './features/profile/SkinUnlockToast'
import { UnlockToast } from './components/UnlockToast'
import { SaveThisSheet } from './components/SaveThisSheet'
import { MusicDirector } from './juice/MusicDirector'
import { OffTabHold } from './components/OffTabHold'
import { NowPlaying } from './components/NowPlaying'
import { BottomNav } from './components/BottomNav'
import { PlayerCardProvider } from './components/PlayerCardModal'
import { AccountWall, useAccountLocked, type WallCopy } from './components/AccountWall'
import { captureSource } from './lib/attribution'
import { captureEmailClick } from './lib/emailClick'
import { captureVerseLink } from './lib/verseLink'
import { captureBoardLink } from './lib/boardLink'

// Every screen past the first few loads on demand, so the first paint carries
// the Play tab and the run rather than the whole app (the bundle was one
// 2.66 MB file). Landing, onboarding, sign-in, Play, the run and its result
// stay eager: they are the path a new player takes before anything else.
// `prefetchTabs` warms the other four tabs once the app is idle.
const DailyTriviaScreen = lazy(() => import('./features/daily/DailyTriviaScreen'))
const PracticeQuizScreen = lazy(() => import('./features/practice/PracticeQuizScreen'))
const PracticeResultScreen = lazy(() => import('./features/practice/PracticeResultScreen'))
const ReviewScreen = lazy(() => import('./features/review/ReviewScreen'))
const BuddiesScreen = lazy(() => import('./features/buddies/BuddiesScreen'))
const MailScreen = lazy(() => import('./features/mail/MailScreen'))
const JournalScreen = lazy(() => import('./features/journal/JournalScreen'))
const CalendarScreen = lazy(() => import('./features/liturgy/CalendarScreen'))
const WardrobeScreen = lazy(() => import('./features/wardrobe/WardrobeScreen'))
const PrayerWallScreen = lazy(() => import('./features/prayer/PrayerWallScreen'))
const ChurchesScreen = lazy(() => import('./features/churches/ChurchesScreen'))
const ChurchDirectoryScreen = lazy(() => import('./features/churches/ChurchDirectoryScreen'))
const ChurchTryScreen = lazy(() => import('./features/churches/ChurchTryScreen'))
const ChurchClaimScreen = lazy(() => import('./features/church/ChurchClaimScreen'))
const ChurchScreen = lazy(() => import('./features/church/ChurchScreen'))
const ChurchPublicScreen = lazy(() => import('./features/church/ChurchPublicScreen'))
const ChurchAdminScreen = lazy(() => import('./features/church/admin/ChurchAdminScreen'))
const ChurchVerseScreen = lazy(() => import('./features/church/ChurchVerseScreen'))
const LeadRedirect = lazy(() => import('./features/church/admin/LeadRedirect'))
const AdminScreen = lazy(() => import('./features/admin/AdminScreen'))
const UnsubscribeScreen = lazy(() => import('./features/email/UnsubscribeScreen'))
const LeaderboardScreen = lazy(() => import('./features/leaderboard/LeaderboardScreen'))
const CollectionScreen = lazy(() => import('./features/collection/CollectionScreen'))
const ProfileScreen = lazy(() => import('./features/profile/ProfileScreen'))
const BattleHub = lazy(() => import('./features/arena/BattleHub'))
const BattleNew = lazy(() => import('./features/arena/BattleNew'))
const BattlePlay = lazy(() => import('./features/arena/BattlePlay'))
const BattleDetail = lazy(() => import('./features/arena/BattleDetail'))
const BattleCpu = lazy(() => import('./features/arena/BattleCpu'))
const LiveLobby = lazy(() => import('./features/arena/LiveBattle'))
const LiveRoom = lazy(() => import('./features/arena/LiveBattle').then((m) => ({ default: m.LiveRoom })))
const ArcadeLobby = lazy(() => import('./features/arcade/ArcadeLobby'))
const ArcadeInvite = lazy(() => import('./features/arcade/ArcadeInvite'))
const MannaScreen = lazy(() => import('./features/arcade/MannaScreen'))
const WordCatchScreen = lazy(() => import('./features/arcade/WordCatchScreen'))
const StudyScreen = lazy(() => import('./features/study/StudyScreen'))
const StudyReportsScreen = lazy(() => import('./features/study/StudyReportsScreen'))
const StudyRecentScreen = lazy(() => import('./features/study/StudyRecentScreen'))
const StudyBagScreen = lazy(() => import('./features/study/StudyBagScreen'))
const TriviaRoundScreen = lazy(() => import('./features/study/TriviaRoundScreen'))
const CrossWordScreen = lazy(() => import('./features/arcade/CrossWordScreen'))
const BibleScreen = lazy(() => import('./features/bible/BibleScreen'))
const BibleBookScreen = lazy(() => import('./features/bible/BibleBookScreen'))
const BibleChapterScreen = lazy(() => import('./features/bible/BibleChapterScreen'))
const HighlightsScreen = lazy(() => import('./features/bible/HighlightsScreen'))
const StampsScreen = lazy(() => import('./features/bible/StampsScreen'))
const SealsScreen = lazy(() => import('./features/bible/SealsScreen'))
const FocusPracticeScreen = lazy(() => import('./features/practice/FocusPracticeScreen'))
const PilgrimageScreen = lazy(() => import('./features/season/PilgrimageScreen'))

function RequireProfile({ children }: { children: JSX.Element }) {
  const { ready, profile } = useAuth()
  if (!ready) return <Splash />
  // Landing, not /welcome: someone without a profile is either brand new or
  // just signed out, and only Landing offers both doors ("Play today's verse"
  // and "I already have an account"). Onboarding assumes you're new.
  if (!profile) return <Navigate to="/" replace />
  return children
}

// The leadership dashboard's own front door. It is opened signed out on
// purpose — "Plan on a bigger screen" sends a leader from the phone app to a
// desktop browser with no session — so instead of Landing it parks the church
// and opens sign-in; `ChurchResume` brings them back here once signed in. A
// keyless build has no sign-in to offer and keeps RequireProfile's rule.
function RequireLeaderSession({ children }: { children: JSX.Element }) {
  const { ready, profile, mode } = useAuth()
  const { id } = useParams()
  if (!ready) return <Splash />
  if (isSupabaseConfigured && id && (mode !== 'online' || !profile)) {
    setPendingChurchAdmin(id)
    return <Navigate to="/auth" replace />
  }
  return <RequireProfile>{children}</RequireProfile>
}

// What a guest can reach, and what asks for a free account first.
//
// The product rule, stated once so it isn't guessed from the route table: a
// guest gets TODAY'S VERSE and THEIR OWN PROFILE. Everything else — battles,
// the keep, Study, the Bible, the church, the road, ranks, cards, buddies —
// wants an account, because every one of those either pools with other people
// or is a record that has to survive a lost phone.
//
// The gate lives here rather than in the screens on purpose: one list, one
// place to read it, and the screens keep whatever guest behaviour they already
// had (BattleHub and ChurchScreen still carry theirs), so lifting a wall is
// deleting one wrapper. See components/AccountWall.tsx for why this doesn't
// contradict the two-mode invariant in CLAUDE.md, and why a keyless LOCAL build
// is never walled.
const WALL: Record<string, WallCopy> = {
  battle: {
    icon: '\u2694\ufe0f',
    title: 'Battles need an account',
    line: 'Challenging a friend and the ranks both live on your account \u2014 so a score means the same thing on both your phones.',
  },
  study: {
    icon: '\ud83d\udcda',
    title: 'Study needs an account',
    line: 'Replays, focus drills and the verses you\u2019re keeping are a record of what you\u2019ve worked on. An account is what makes that record yours tomorrow.',
  },
  bible: {
    icon: '\ud83d\udcd6',
    title: 'Your Bible needs an account',
    line: 'Every chapter you open and verse you keep is marked on your own Bible \u2014 31,102 slots that fill in as you play. That belongs on an account, not on one device.',
  },
  cross: {
    icon: '\u271d\ufe0f',
    title: 'The Cross Word needs an account',
    line: 'Every cross you finish marks its verse studied on your own Bible \u2014 that record is the thing an account still has for you tomorrow. The rest of the arcade is open either way.',
  },
  church: {
    icon: '\u26ea',
    title: 'Playing for a church needs an account',
    line: 'Your points pool with everyone else who goes there, and the building grows for all of them. There\u2019s no way to do that from one phone alone.',
  },
  road: {
    icon: '\ud83c\udf3e',
    title: 'The Harvest Road needs an account',
    line: 'The season\u2019s road, its waystations and everything you earn walking it are saved to your account.',
  },
  ranks: {
    icon: '\ud83c\udfc6',
    title: 'Ranks need an account',
    line: 'A board of one person isn\u2019t a board. Create a free account to stand on it.',
  },
  cards: {
    icon: '\ud83c\udccf',
    title: 'Your collection needs an account',
    line: 'Verse cards, relics and skins are yours to keep \u2014 an account is what keeps them.',
  },
  mail: {
    icon: '\ud83d\udcec',
    title: 'Your mailbox needs an account',
    line: 'Gifts, buddy requests and the news a season brings are addressed to a person. An account is the address.',
  },
  buddies: {
    icon: '\ud83e\udd1d',
    title: 'Bible Buddies need an account',
    line: 'Adding a friend takes two accounts. Yours is free.',
  },
  review: {
    icon: '\ud83e\udde0',
    title: 'Keeping verses needs an account',
    line: 'Reviews come back on a schedule built from what you got wrong \u2014 that schedule has to outlive this browser tab.',
  },
  wall: {
    icon: '\ud83d\udd6f\ufe0f',
    title: 'The Prayer Wall needs an account',
    line: 'A note needs a stranger to kneel at it, and a candle needs a stranger\u2019s note. Two accounts \u2014 yours is free.',
  },
  replay: {
    icon: '\ud83d\udd01',
    title: 'Replays need an account',
    line: 'Today\u2019s verse is yours to play. Every day before it, and beating your own best on them, comes with an account.',
  },
}

function RequireAccount({ copy, children }: { copy: WallCopy; children: JSX.Element }) {
  const locked = useAccountLocked()
  if (locked) return <AccountWall {...copy} />
  return children
}

function Splash() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
      <div className="floaty" style={{ fontSize: 64 }}>
        📖
      </div>
    </div>
  )
}

// The five tab routes share the bottom nav.
// Its own Suspense, so a tab whose chunk is still arriving keeps the nav.
function TabShell({ children }: { children: JSX.Element }) {
  return (
    <>
      <Suspense fallback={<RouteLoading />}>{children}</Suspense>
      <BottomNav />
    </>
  )
}

// What a lazy screen shows for the moment its chunk takes to arrive: nothing
// but reserved space. A spinner or the splash's book would flash on every
// first visit to a screen; on the App Store build the chunk is on the device
// and this is a frame or two.
function RouteLoading() {
  return <div aria-busy="true" style={{ minHeight: '60vh' }} />
}

// The four other tabs, fetched once the first screen has painted and the
// browser is idle, so the first tap on Battle or Study doesn't wait on the
// network. Fire-and-forget: a failed prefetch only means the tap fetches it.
function prefetchTabs() {
  const go = () => {
    void import('./features/arena/BattleHub').catch(() => {})
    void import('./features/study/StudyScreen').catch(() => {})
    void import('./features/church/ChurchScreen').catch(() => {})
    void import('./features/profile/ProfileScreen').catch(() => {})
  }
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }
  if (w.requestIdleCallback) w.requestIdleCallback(go, { timeout: 4000 })
  else setTimeout(go, 2500)
}

export default function App() {
  const init = useAuth((s) => s.init)
  const ready = useAuth((s) => s.ready)
  const mode = useAuth((s) => s.mode)
  const profileId = useAuth((s) => s.profile?.id)
  const navigate = useNavigate()
  useJuiceSync()
  useEffect(() => { prefetchTabs() }, [])

  // iOS remote push: hand the server a fresh APNs token for whoever is signed
  // in, on every sign-in. Silent — it only re-registers when the permission
  // is already granted and never shows the OS prompt (PushNudge / Settings do).
  useEffect(() => {
    if (ready && mode === 'online' && profileId) void refreshPushToken()
  }, [ready, mode, profileId])

  // Pull buddy requests once the account is up, app-wide rather than on the You
  // tab. The nav dot has to be able to say "someone is waiting" from the Play
  // tab — loading this only where the list is rendered is what made a pending
  // request invisible until you'd already gone looking for it.
  useEffect(() => {
    if (ready && mode === 'online') {
      void useBuddies.getState().load()
      // Gifts, for the same reason: the nav dot has to be able to say "there's
      // something for you" from the Play tab, and loading this only where the
      // mailbox renders would make a gift invisible until you'd already gone
      // looking for it.
      void useGifts.getState().load()
      // A church check-in (0137) lights the same mailbox dot.
      void useChurchCheckIns.getState().load()
      // A church's reply to your inquiry (0155) lights it too.
      void useChurchReplies.getState().load()
    }
  }, [ready, mode])

  useEffect(() => {
    // Capture a referral code from the invite link (?ref=CODE) before anything
    // else, so it survives the guest→signup flow and gets applied on account load.
    try {
      const ref = new URLSearchParams(window.location.search).get('ref')
      if (ref) localStorage.setItem('va.ref', ref.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))
    } catch {
      /* ignore */
    }
    // And which network sent them (?src=tiktok…), same lifetime, same reason.
    captureSource()
    // And which day's verse the post was about (?d=…), so the link opens it.
    captureVerseLink()
    // And a quiz or myth post's ?b=1, which brings them to yesterday's board.
    captureBoardLink()
    // And a tap on a come-back email's link (?e=…, 0174), counted once.
    captureEmailClick()
    init()
    // The content catalog, BEFORE the season store reads it. Its cache is
    // already applied at import (store/catalog.ts), so this only refreshes;
    // whichever way it goes, activeRoad() has something to answer with.
    void useCatalog.getState().load()
    // The road, app-wide rather than on the Play tab: the streak flame, the
    // Daily Chest and the confetti engine all read equipped seasonal cosmetics,
    // and none of those is behind the strip that opens the road.
    void useSeason.getState().load()
    // Local reminders (native only) — load the device's preferences and lay down
    // the schedule. No-op on the web, where Web Push handles this instead.
    void useReminders.getState().load()
    // Handle the OAuth redirect deep link (com.versearcade.app://auth/callback)
    // returning from Sign in with Google/Apple on device.
    initNative(async (url) => {
      if (url.includes('auth/callback') || url.includes('code=')) {
        await useAuth.getState().completeNativeOAuth(url)
        // Native OAuth finishes here, asynchronously, long after the user tapped
        // "Sign in with Apple/Google" — so the sign-in screen can't navigate on
        // its own. Once the session + profile are in, move into the app.
        if (useAuth.getState().profile) navigate(firstStop(useAuth.getState().profile), { replace: true })
        return
      }
      // Any other link into the app opens the screen it names (lib/native).
      const path = inAppPath(url)
      if (path) navigate(path)
    }, () => {
      // Back in the foreground: rebuild the reminder plan against the current
      // review state and roll the horizon forward.
      void useReminders.getState().reschedule()
    }, (path) => navigate(path))
  }, [init, navigate])

  if (!ready) return <Splash />

  return (
    <PlayerCardProvider>
    {/* Keeps scrolled content from colliding with the clock. Inert and 0px
        tall wherever there is no notch — see .status-scrim in index.css. */}
    <div className="status-scrim" aria-hidden />
    <BattleResume />
    <ChurchResume />
    {/* A study run finishes and immediately navigates, so the reveal for
        anything it turned up is mounted here and follows the player. */}
    <StudyDropToast />
    {/* Reaching a waystation reveals here for the same reason: a run navigates
        the instant it finishes, so the reveal has to follow the player. */}
    <WaystationToast />
    {/* Mounted app-wide: the winner of an async battle isn't on any
        particular screen when their battle completes. See store/skinUnlocks. */}
    <SkinUnlockToast />
    {/* And a piece for one of the two rooms — see store/unlocks.ts. */}
    <UnlockToast />
    {/* The account ask, made once, at the first thing a guest earns — see
        store/saveNudge.ts. */}
    <SaveThisSheet />
    {/* The soundtrack follows the route rather than any one screen, so it lives
        up here with the other app-wide passengers. */}
    <MusicDirector />
    {/* The track card and the room-piece toast wait for a tab root, where the
        top-left corner is not somebody's back button. */}
    <OffTabHold />
    <NowPlaying />
    <Suspense fallback={<RouteLoading />}>
    <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/welcome" element={<Onboarding />} />
        <Route path="/auth" element={<AuthScreen />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* Public: the link at the foot of an email carries no session (0169). */}
        <Route path="/unsubscribe" element={<UnsubscribeScreen />} />

        <Route
          path="/play"
          element={
            <RequireProfile>
              <TabShell>
                <HomeScreen />
              </TabShell>
            </RequireProfile>
          }
        />
        <Route
          path="/play/run"
          element={
            <RequireProfile>
              <QuizScreen />
            </RequireProfile>
          }
        />
        <Route
          path="/play/result"
          element={
            <RequireProfile>
              <ResultScreen />
            </RequireProfile>
          }
        />
        {/* The day's trivia round — the Play tab's second box. Guest-open like
            the rest of /play: it pays no XP and nothing rankable, and the one
            thing it records (the verse, marked studied) has a guest path. */}
        <Route
          path="/play/trivia"
          element={
            <RequireProfile>
              <DailyTriviaScreen />
            </RequireProfile>
          }
        />
        {/* Today's verse from your church — its own lane beside the global
            drop, never in place of it (0128). Members of a verified church
            only; the screen says so plainly to anybody else. */}
        <Route
          path="/play/church"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.church}>
                <ChurchVerseScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/play/practice/:date"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.replay}>
                <PracticeQuizScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/play/practice/:date/result"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.replay}>
                <PracticeResultScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/review"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.review}>
                <ReviewScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/buddies"
          element={
            <RequireProfile>
              <TabShell>
                <RequireAccount copy={WALL.buddies}>
                  <BuddiesScreen />
                </RequireAccount>
              </TabShell>
            </RequireProfile>
          }
        />
        {/* The Journal — what you have done. Open to a guest, like /you itself:
            it is derived entirely from numbers this device already has, so it
            reads correctly with no account and walls nothing off. */}
        {/* The church year. Open to a guest and behind no wall, like the
            Journal and the Wardrobe: it is purely derived from the date, it
            grants nothing, and what a day IS is not the app's to gate. */}
        <Route
          path="/calendar"
          element={
            <TabShell>
              <CalendarScreen />
            </TabShell>
          }
        />
        <Route
          path="/journal"
          element={
            <RequireProfile>
              <TabShell>
                <JournalScreen />
              </TabShell>
            </RequireProfile>
          }
        />
        <Route
          path="/wardrobe"
          element={
            <RequireProfile>
              <TabShell>
                <WardrobeScreen />
              </TabShell>
            </RequireProfile>
          }
        />
        {/* The mailbox — everything addressed to you, in one place. Reachable
            from the 📬 pill on your own card; not a tab, because a sixth tab
            would not clear a 320px phone. */}
        <Route
          path="/mail"
          element={
            <RequireProfile>
              <TabShell>
                <RequireAccount copy={WALL.mail}>
                  <MailScreen />
                </RequireAccount>
              </TabShell>
            </RequireProfile>
          }
        />
        {/* The Prayer Wall — leave a note, or hold a candle for somebody
            else's. Online-only like washing feet, for the same reason: it
            needs a second account on the other end. */}
        <Route
          path="/pray"
          element={
            <RequireProfile>
              <TabShell>
                <RequireAccount copy={WALL.wall}>
                  <PrayerWallScreen />
                </RequireAccount>
              </TabShell>
            </RequireProfile>
          }
        />
        {/* Church tab — the church you play FOR: pick it, pour points into it,
            watch it level up and climb the board against churches near it. */}
        <Route
          path="/church"
          element={
            <RequireProfile>
              <TabShell>
                <RequireAccount copy={WALL.church}>
                  <ChurchScreen />
                </RequireAccount>
              </TabShell>
            </RequireProfile>
          }
        />
        {/* For Churches — the B2B congregation-partnership funnel that replaced
            the Groups tab. Public so it can be shared/linked without an account.
            Distinct from /church above, which is the player-facing tab. */}
        <Route
          path="/pilgrimage"
          element={
            <RequireProfile>
              <TabShell>
                <RequireAccount copy={WALL.road}>
                  <PilgrimageScreen />
                </RequireAccount>
              </TabShell>
            </RequireProfile>
          }
        />
        {/* Public: one congregation, linkable by anybody — the page a church
            puts on a slide. Handles its own gate + signup resume exactly like
            /battle/:id, so it is NOT wrapped in RequireProfile or the wall.
            Distinct from /church above (the player-facing tab) and /churches
            below (the B2B funnel). */}
        <Route path="/church/:id" element={<ChurchPublicScreen />} />
        {/* Public: a pastor's one-time leadership invite (0121). Handles its
            own sign-up resume, like the page above. */}
        <Route path="/church/:id/claim" element={<ChurchClaimScreen />} />
        {/* A church's leadership tools. Behind the church wall (an account) —
            and every RPC under it re-checks leadership server-side, so the
            route is a door, not a lock. */}
        {/* The map's Leadership row: resolves your church, then /admin. */}
        <Route
          path="/church/lead"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.church}>
                <LeadRedirect />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/church/:id/admin"
          element={
            <RequireLeaderSession>
              <RequireAccount copy={WALL.church}>
                <ChurchAdminScreen />
              </RequireAccount>
            </RequireLeaderSession>
          }
        />
        <Route path="/churches" element={<ChurchesScreen />} />
        {/* Public: every church on the app, by name — the free layer's front
            door. Open to a guest for the reason /church/:id is: somebody who
            heard about their church on a Sunday should be able to find it
            before being asked for anything. */}
        <Route path="/churches/find" element={<ChurchDirectoryScreen />} />
        {/* Public: the leadership tools on a church that exists only in the
            page's memory, so a pastor can try them before signing up. Nothing
            is written anywhere — see the screen's header. */}
        <Route path="/churches/try" element={<ChurchTryScreen />} />
        {/* Old Groups deep links now land on the churches page. */}
        <Route path="/groups" element={<Navigate to="/churches" replace />} />
        {/* Private operator surface — gated to the admin account + PIN inside. */}
        <Route
          path="/admin"
          element={
            <RequireProfile>
              <AdminScreen />
            </RequireProfile>
          }
        />
        <Route
          path="/battle"
          element={
            <RequireProfile>
              <TabShell>
                <RequireAccount copy={WALL.battle}>
                  <BattleHub />
                </RequireAccount>
              </TabShell>
            </RequireProfile>
          }
        />
        {/* The arcade. The cabinet standing in the hall, the churchyard and your
            own Upper Room opens the LOBBY — there is more than one machine in
            there, and a door that always led to the same game would be lying
            about what the arcade is.

            Guest-open, like the games that persist nothing. The one exception
            is the Cross Word, which marks its verse studied on the player's own
            Bible: that's a record rather than a round, so it asks for an
            account the way the rest of the Bible does. Static segments first,
            so a game can never be swallowed by the lobby's own path. */}
        <Route
          path="/arcade"
          element={
            <RequireProfile>
              <ArcadeLobby />
            </RequireProfile>
          }
        />
        <Route
          path="/arcade/manna"
          element={
            <RequireProfile>
              <MannaScreen />
            </RequireProfile>
          }
        />
        <Route
          path="/arcade/word-catch"
          element={
            <RequireProfile>
              <WordCatchScreen />
            </RequireProfile>
          }
        />
        <Route
          path="/arcade/cross"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.cross}>
                <CrossWordScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        {/* A shared machine: one free go for whoever opens the link, then the
            invitation. PUBLIC — no RequireProfile and no wall, because the
            person on the other end of a share may never have seen this app and
            the machine is the pitch. Nothing a free go does is written down
            anywhere (see the games' `demo` prop), so there is nothing an
            account has to exist for first. */}
        <Route path="/arcade/:game/invite" element={<ArcadeInvite />} />
        {/* Study tab — practice surfaces that never touch your rank. */}
        <Route
          path="/study"
          element={
            <RequireProfile>
              <TabShell>
                <RequireAccount copy={WALL.study}>
                  <StudyScreen />
                </RequireAccount>
              </TabShell>
            </RequireProfile>
          }
        />
        {/* The player's own Bible — all 66 books, shaded by what they've kept,
            studied and read. Reading, never scoring. Reached from the profile
            (the book you open) and from Study. */}
        <Route
          path="/bible"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.bible}>
                <BibleScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        {/* Static segment before the dynamic one, so a book can never be named
            "highlights" out from under this. */}
        <Route
          path="/bible/highlights"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.bible}>
                <HighlightsScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/bible/stamps"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.bible}>
                <StampsScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/bible/seals"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.bible}>
                <SealsScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/bible/:book"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.bible}>
                <BibleBookScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/bible/:book/:chapter"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.bible}>
                <BibleChapterScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        {/* The favorites shelf became the highlights page inside the Bible;
            old links and bookmarks still land somewhere true. */}
        <Route path="/favorites" element={<Navigate to="/bible/highlights" replace />} />
        {/* The books Tabitha lends that open onto pages of their own —
            reports (accuracy by book), the last-five replays, and the bag. */}
        <Route
          path="/study/reports"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.study}>
                <StudyReportsScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/study/recent"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.study}>
                <StudyRecentScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/study/bag"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.study}>
                <StudyBagScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        {/* The Cross Word moved into the arcade when the arcade got a lobby.
            Anything already pointing here still lands somewhere true. */}
        <Route path="/study/cross" element={<Navigate to="/arcade/cross" replace />} />
        {/* A whole round of the daily drop's bonus trivia, on a book you pick. */}
        <Route
          path="/study/trivia"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.study}>
                <TriviaRoundScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        {/* Drill one book against a study companion, reached from Study. */}
        <Route
          path="/study/focus"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.study}>
                <FocusPracticeScreen />
              </RequireAccount>
            </RequireProfile>
          }
        />
        {/* Live 1v1: a room code, a ready-check, one clock. Both segments are
            static-first, so they out-rank /battle/:id in the router's ranking. */}
        <Route
          path="/battle/live"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.battle}>
                <LiveLobby />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/battle/live/:code"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.battle}>
                <LiveRoom />
              </RequireAccount>
            </RequireProfile>
          }
        />
        {/* Solo practice battle vs a simulated opponent, reached from Study. */}
        <Route
          path="/battle/cpu"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.study}>
                <BattleCpu />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/battle/new"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.battle}>
                <BattleNew />
              </RequireAccount>
            </RequireProfile>
          }
        />
        <Route
          path="/battle/:id/play"
          element={
            <RequireProfile>
              <RequireAccount copy={WALL.battle}>
                <BattlePlay />
              </RequireAccount>
            </RequireProfile>
          }
        />
        {/* Public: an invite opened by someone without an account handles its own
            gate + signup resume, so it is NOT wrapped in RequireProfile. */}
        <Route path="/battle/:id" element={<BattleDetail />} />
        <Route
          path="/leaderboard"
          element={
            <RequireProfile>
              <TabShell>
                <RequireAccount copy={WALL.ranks}>
                  <LeaderboardScreen />
                </RequireAccount>
              </TabShell>
            </RequireProfile>
          }
        />
        <Route
          path="/collection"
          element={
            <RequireProfile>
              <TabShell>
                <RequireAccount copy={WALL.cards}>
                  <CollectionScreen />
                </RequireAccount>
              </TabShell>
            </RequireProfile>
          }
        />
        <Route
          path="/you"
          element={
            <RequireProfile>
              <TabShell>
                <ProfileScreen />
              </TabShell>
            </RequireProfile>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </PlayerCardProvider>
  )
}

// Supabase parses the OAuth hash automatically (detectSessionInUrl); we just
// wait for the profile to load, then bounce into the app.
function AuthCallback() {
  const { profile, ready } = useAuth()
  if (ready && profile) return <Navigate to={firstStop(profile)} replace />
  return <Splash />
}
